import { FC, useEffect, useState } from 'react';

// API
import apibridge from '../../apibridge';

// REDUX
import { useAppDispatch, useAppSelector } from '../../store/slice';
import { ToastMessagesSlice } from '../../store/slice/ToastMessages';
import { UserType } from '../../store/slice/User';

// PACKAGES
import Moment from 'moment';
import { Offcanvas, Spinner } from 'react-bootstrap';

// COMPONENTS
import TitleSection from '../../components/TitleSection/TitleSection';
import DelayedFadeIn from '../../components/_Helpers/DelayedFadeIn';
import RichText from '../../components/RichText/RichText';
import SvgMask from '../../components/_Helpers/SvgMask';

// UTILS
import { guid } from '../../libs/utils';

// TYPES
import { AlertListAlert } from '../../api/models';

const Alerts: FC = () => {
	const dispatch = useAppDispatch();
	const user: UserType = useAppSelector((state) => state.user);

	const [isLoading, setIsLoading] = useState(true);

	// State to show/hide OffCanvas
	const [showAlertOffCanvas, setShowAlertOffCanvas] = useState(false);

	// State to store the alert list
	const [alertList, setAlertList] = useState<AlertListAlert[]>([]);
	const [alertDetail, setAlertDetail] = useState<AlertListAlert>();

	const getAlertList = async () => {
		setIsLoading(true);

		const response = await apibridge.getAlertList();

		if (response && response.data) {
			if (!response.data.isError && response.data.result) {
				setAlertList(response.data.result.alerts || []);
			} else if (response.data.validationErrors) {
				for (const err of response.data.validationErrors) {
					dispatch(
						ToastMessagesSlice.actions.add({
							id: guid(),
							type: 'danger',
							heading: 'Alerts error',
							description: err.reason || 'Unknown error'
						})
					);
				}
			}
		}

		setIsLoading(false);
	};

	useEffect(() => {
		getAlertList();
	}, []); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<div className="page-alerts d-flex flex-column flex-grow-1">
			<TitleSection title="Alerts" description=""></TitleSection>

			<div className="container h-100">
				{isLoading ? (
					<div className="d-flex align-items-center justify-content-center position-relative w-100">
						<DelayedFadeIn>
							<Spinner animation="border" role="status">
								<span className="visually-hidden">Loading...</span>
							</Spinner>
						</DelayedFadeIn>
					</div>
				) : user.permissions?.includes('Alert') ? (
					<>
						{alertList?.length > 0 ? (
							alertList.map((alertItem, alertItemIndex) => {
								return (
									<div className="alert-row" key={alertItem.id || alertItemIndex}>
										<div className="alert-icon">
											<SvgMask path="/svg/bell.svg" width={24} height={24} />
										</div>

										<div className="flex-grow-1">
											<span className="body-tiny fw-semibold text-shades-500 mb-1">
												{alertItem.publishDate ? Moment(alertItem.publishDate).format('DD MMMM YYYY') : ''}
											</span>

											<p className="mb-0 fw-bold text-dark-text mb-1">{alertItem.title}</p>

											{alertItem.description ? <RichText content={{ markup: alertItem.description }} /> : null}
										</div>

										<button
											className="btn btn-link flex-shrink-0"
											onClick={() => {
												setShowAlertOffCanvas(true);
												setAlertDetail(alertItem);
											}}
										>
											View details
										</button>
									</div>
								);
							})
						) : (
							<div className="d-flex h-100 flex-column justify-content-center align-items-center">
								<p className="text-dark-text fw-bold mb-0 text-center">
									No announcements or alerts have been made yet.
								</p>
							</div>
						)}
					</>
				) : (
					<div className="d-flex align-items-center justify-content-center h-100">
						<h3 className="text-center text-shades-500 m-0">You don't have permission to view this content.</h3>
					</div>
				)}
			</div>

			<Offcanvas
				bsPrefix="view-alert offcanvas"
				show={showAlertOffCanvas}
				onHide={() => setShowAlertOffCanvas(false)}
				placement="end"
			>
				<Offcanvas.Header closeButton>
					<Offcanvas.Title className="d-flex justify-content-start align-items-center w-100">
						<h2 className="text-shades-800 mb-0">
							<strong>{alertDetail?.title}</strong>
						</h2>
					</Offcanvas.Title>
				</Offcanvas.Header>

				<Offcanvas.Body className="d-flex flex-column">
					{isLoading ? (
						<div className="d-flex align-items-center justify-content-center w-100">
							<DelayedFadeIn>
								<Spinner animation="border" role="status">
									<span className="visually-hidden">Loading...</span>
								</Spinner>
							</DelayedFadeIn>
						</div>
					) : (
						<> {alertDetail?.content ? <RichText content={{ markup: alertDetail.content }} /> : null}</>
					)}
				</Offcanvas.Body>
			</Offcanvas>
		</div>
	);
};

export default Alerts;
